import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Label } from "@volca/ui/components/ui/label";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { toast } from "@volca/ui/lib/toast";
import { canManageReport } from "@/profitability/access";
import { useRebuildReport } from "@/profitability/report-query";
import type { ProfitabilityReport } from "@/profitability/types";
import { reportError } from "@/profitability/utils";

export const DescriptionEditor = ({
  businessId,
  report,
}: {
  businessId: string;
  report: ProfitabilityReport;
}) => {
  const rebuild = useRebuildReport(businessId);
  const [draft, setDraft] = useState<string | null>(null);
  // Editing rebuilds the template, which is finance's.
  const editable =
    canManageReport(report) &&
    report.build_status !== "building" &&
    report.sample_documents.length > 0;
  const changed =
    draft !== null &&
    draft.trim().length > 0 &&
    draft.trim() !== (report.description ?? "").trim();

  if (draft === null)
    return (
      <Stack align="stretch" gap={2}>
        <Stack direction="row" gap={2} align="center" justify="between">
          <Text render={<h3 />} variant="label">
            Agent instructions
          </Text>
          {editable && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDraft(report.description ?? "")}
            >
              Edit
            </Button>
          )}
        </Stack>
        <Text
          variant="body"
          className="max-w-prose [overflow-wrap:anywhere] whitespace-pre-line"
        >
          {report.description || "No build instructions saved."}
        </Text>
      </Stack>
    );

  return (
    <Stack align="stretch" gap={2} data-testid="description-editor">
      <Label htmlFor="template-description">Agent instructions</Label>
      <Textarea
        id="template-description"
        rows={12}
        value={draft}
        disabled={rebuild.isPending}
        onChange={(event) => setDraft(event.target.value)}
      />
      <Text variant="caption" tone="muted">
        {report.published
          ? "Build a draft with these instructions. Review its sample report before publishing."
          : "Build this agent from the same sample files with these instructions."}
      </Text>
      <Stack direction="row" gap={2} wrap>
        <Button
          disabled={!changed || !editable}
          loading={rebuild.isPending}
          onClick={() =>
            rebuild.mutate(
              { reportId: report.id, description: draft.trim() },
              {
                onSuccess: () => {
                  setDraft(null);
                  toast.success(`Building a draft for ${report.name}`);
                },
                onError: (error) =>
                  toast.error("The draft could not start. Try again.", {
                    description: reportError(error),
                  }),
              },
            )
          }
        >
          Build draft
        </Button>
        <Button
          variant="secondary"
          disabled={rebuild.isPending}
          onClick={() => setDraft(null)}
        >
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};
